// Central registry of product-analytics event names (PostHog).
// Covers the activation funnel: made plan → saw alternatives → tweaked →
// activated → connected → returned. Capture via usePostHog(); events are
// no-ops when analytics consent is withheld.
export const AnalyticsEvent = {
  // Sign-up funnel: the auth page (/signup, /signin) is the last step of
  // every marketing CTA, so viewed → method selected → sign_up_completed
  // measures whether the page converts the CTA's click. Bounce is a view
  // with no completion (PostHog $pageleave closes the funnel); events carry
  // signup_intent/cta_label/from so conversion splits by intent and CTA.
  // (auth_drawer_opened/auth_drawer_dismissed were the drawer-era
  // equivalents — retired with the drawer, kept in history.)
  AuthPageViewed: 'auth_page_viewed',
  AuthMethodSelected: 'auth_method_selected',
  SignUpCompleted: 'sign_up_completed',

  // Meal planning wizard
  WizardStepViewed: 'wizard_step_viewed',
  WizardStepCompleted: 'wizard_step_completed',
  WizardCancelled: 'wizard_cancelled',
  MealPlanCreated: 'meal_plan_created',

  // "Use last week's request" chip on the wizard's extras step. Shipped as an
  // offer rather than a prefill because a prefill would have been correct in
  // only 6 of 84 measured plan-to-plan transitions; used/shown is the
  // evidence that would justify revisiting that, and dismissed says it is
  // in the way.
  LastRequestChipShown: 'last_request_chip_shown',
  LastRequestChipUsed: 'last_request_chip_used',
  LastRequestChipDismissed: 'last_request_chip_dismissed',

  // Wizard picker (step_id "picker" in the wizard events above)
  PickerRecipeLocked: 'picker_recipe_locked',
  PickerRecipeUnlocked: 'picker_recipe_unlocked',
  PickerSkipped: 'picker_skipped',
  PickerRefreshed: 'picker_refreshed',
  PickerNudgeShown: 'picker_nudge_shown',
  PickerNudgeEngaged: 'picker_nudge_engaged',
  // Fired when a "New for you" shortlist renders (initial load and each
  // spin), carrying its retrieval-source composition so exploration share is
  // a monitored metric — learned taste shapes retrieval, so exploration
  // representation must be observed, not assumed.
  PickerShortlistShown: 'picker_shortlist_shown',

  // Tweak loop
  AlternativeViewed: 'alternative_viewed',
  AlternativeSelected: 'alternative_selected',
  MoreIdeasChipClicked: 'more_ideas_chip_clicked',
  RecipeMenuOpened: 'recipe_menu_opened',
  SubstitutionRequested: 'substitution_requested',
  FirstSwapPromptShown: 'first_swap_prompt_shown',
  FirstSwapPromptEngaged: 'first_swap_prompt_engaged',
  FirstSwapPromptDismissed: 'first_swap_prompt_dismissed',
  CustomMealAdded: 'custom_meal_added',
  CustomMealEdited: 'custom_meal_edited',
  AssignmentRemoved: 'assignment_removed',

  // Feature announcements (one-time release modals on the dashboard)
  FeatureAnnouncementShown: 'feature_announcement_shown',
  FeatureAnnouncementCtaClicked: 'feature_announcement_cta_clicked',
  FeatureAnnouncementDismissed: 'feature_announcement_dismissed',

  // Activation & integrations
  MealPlanActivated: 'meal_plan_activated',
  KrogerConnected: 'kroger_connected',
} as const;

export type SubstitutionType = 'reroll' | 'instructions' | 'cookbook';
